/**
 * Where a newly typed task gets filed.
 *
 * `tasks.area_id` is NOT NULL (§7) and the server will not guess one — a task submitted
 * with no area is held in the inbox instead (§0). So anything in the UI that creates a task
 * from a bare title has to decide what to send, and they should all decide the same way.
 *
 * The remembered value is the user's own last explicit choice, which is different in kind
 * from the server picking an area on their behalf.
 *
 * **Staleness.** Reported directly: remembering forever is a trap, not a convenience — file
 * a quick task after coming back to a tab left open since this morning, and it silently
 * lands wherever the *last* session happened to leave the picker, with nothing to notice.
 * `STALE_AFTER_MS` bounds how long a remembered choice stays trustworthy; past it,
 * `recalledDestination` answers "" (inbox) as if nothing had ever been picked, the same
 * answer a first run gets. The timestamp travels with the value rather than living
 * separately, so there is never a version skew between "what was picked" and "when."
 */

const LAST_DEST_KEY = "efss.lastDestination";

/** An hour is long enough that a burst of adds under one project — the whole point of
 * remembering at all — never trips it, but short enough that coming back from a lunch
 * break, let alone overnight, always starts back at inbox rather than silently reusing
 * whatever was last picked. */
const STALE_AFTER_MS = 60 * 60 * 1000;

/** `""` (inbox), `area:<id>`, or `project:<id>`. */
export type Destination = string;

type Stored = { value: Destination; at: number };

function readStored(): Stored | null {
  try {
    const raw = window.localStorage.getItem(LAST_DEST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    // Also catches the pre-staleness format (a bare destination string): `JSON.parse` on
    // plain text without a leading `{`/`"` throws, which the catch below turns into "never
    // remembered" — a safe, one-time forget on upgrade, not a crash.
    if (typeof parsed.value !== "string" || typeof parsed.at !== "number") return null;
    return parsed as Stored;
  } catch {
    return null;
  }
}

export function rememberDestination(value: Destination): void {
  try {
    window.localStorage.setItem(LAST_DEST_KEY, JSON.stringify({ value, at: Date.now() }));
  } catch {
    /* storage disabled; the picker just won't remember */
  }
}

/** The remembered destination, or `""` if it's gone stale or nothing was ever remembered —
 * the same fallback either way, so a caller never has to tell the two apart. */
export function recalledDestination(): Destination {
  const stored = readStored();
  if (!stored || Date.now() - stored.at > STALE_AFTER_MS) return "";
  return stored.value;
}

/** Whether the remembered destination has aged past `STALE_AFTER_MS`, without altering it —
 * for a caller that already has a value in hand (component state seeded from an earlier,
 * possibly-stale `recalledDestination` call) and needs to know whether to re-derive it
 * before using it for real, rather than trusting what it was handed at mount. */
export function isDestinationStale(): boolean {
  const stored = readStored();
  return stored !== null && Date.now() - stored.at > STALE_AFTER_MS;
}

/** Turn a picker value into the fields `POST /api/tasks` expects. */
export function parseDestination(value: Destination): {
  area_id?: string;
  project_id?: string;
} {
  if (value.startsWith("area:")) return { area_id: value.slice(5) };
  if (value.startsWith("project:")) return { project_id: value.slice(8) };
  return {};
}
